import React, { useEffect } from "react";
import { RouteComponentProps, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import * as ConfigurationActions from "../../../store/configurationService/actions";
import { useTranslation } from "react-i18next";
type CreditCardComparisonCalculatorProps = RouteComponentProps<{}>;

const CreditCardComparisonCalculator: React.FunctionComponent<CreditCardComparisonCalculatorProps> = (props) => {
    const { t: tLang, i18n: i18nLang } = useTranslation();
    const dispatch = useDispatch();
    const routeLoading = useSelector((state: ApplicationState) => state.configurationState && (state.configurationState.loading || false));

    useEffect(() => {
        if (routeLoading) {
            dispatch(ConfigurationActions.actionCreators.setRouteLoading(false));
        }
    }, [dispatch, routeLoading]);

    function handleSubmit(e: any) {
        e.preventDefault();
        e.stopPropagation();
    }

    return (
        <React.Fragment>
            <div className=" row">
                <div className="col-xs-12">
                    <h1 className="title-page">{tLang("resourcesSection.calculator.label.creditCardComparisonCalculator")}</h1>
                </div>
            </div>

            <div className="row" id="content">
                <div className="col-xs">
                    <div className="box offset-padding">
                        <div className="table-container scroll">
                            <div id="calc-body">
                                <div id="container_outer">
                                    <div>
                                        <Link id="close" to="/resources/calculators">
                                            X
                                        </Link>
                                    </div>

                                    <div className="calc_container">
                                        <div className="calc_contents">
                                            <div className="calc_description">
                                                <h3 className="financial" style={{ paddingBottom: "20px", paddingTop: "20px" }}>
                                                    Credit Card Comparison Calculator
                                                </h3>
                                                <p>
                                                    <strong>
                                                        This calculator will help you to compare the long-term costs of credit cards that have varying interest
                                                        rates, annual fees, introductory rates and compounding intervals.
                                                    </strong>
                                                </p>
                                                <p>
                                                    Simply enter a balance amount, a payment amount, and the terms for each card, then click the "Compute" button.
                                                    Please note that fixed payments (the same amount for all payments) are used to compare the costs of the cards,
                                                    so if you ever pay less than the entered payment amount, your actual costs will vary from the results of this
                                                    calculator.
                                                </p>
                                            </div>

                                            <form onSubmit={(e) => handleSubmit(e)}>
                                                <table className="calculator auto">
                                                    <tbody>
                                                        <tr>
                                                            <td colSpan={2}><label htmlFor="principal"><strong>Enter a balance amount to use to make comparison:</strong></label></td>
                                                            <td>
                                                                <input type="TEXT" name="principal" id="principal" aria-labelledby="principal" size={15} />
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td colSpan={2}><label htmlFor="payment"><strong>Enter a monthly payment amount:<br />
                                                                <span className="note2">Must be greater than monthly interest charge</span>
                                                            </strong></label></td>
                                                            <td>
                                                                <input type="TEXT" name="payment" id="payment" aria-labelledby="payment" size={15} />
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <th className="calc_divider">
                                                                <strong>Comparison</strong>
                                                            </th>
                                                            <th className="calc_divider">
                                                                <strong>Card #1</strong>
                                                            </th>
                                                            <th className="calc_divider">
                                                                <strong>Card #2</strong>
                                                            </th>
                                                        </tr>

                                                        <tr>
                                                            <td><label htmlFor="annFee1"><strong>Annual fee (optional):</strong></label></td>
                                                            <td><input type="TEXT" name="annFee1" id="annFee1" aria-labelledby="annFee1" size={15} /></td>
                                                            <td>
                                                                <label htmlFor="annFee2" className="hide-label"><strong>Annual fee (optional): &nbsp;</strong></label>
                                                                <input type="TEXT" name="annFee2" id="annFee2" aria-labelledby="annFee2" size={15} />
                                                            </td>

                                                        </tr>

                                                        <tr>
                                                            <td><label htmlFor="introIntRate1"><strong>Enter introductory annual interest rate (optional):</strong></label></td>
                                                            <td><input type="TEXT" name="introIntRate1" id="introIntRate1" aria-labelledby="introIntRate1" size={15} /></td>
                                                            <td>
                                                                <label htmlFor="introIntRate2" className="hide-label"><strong>Enter introductory annual interest rate (optional): &nbsp;</strong></label>
                                                                <input type="TEXT" name="introIntRate2" id="introIntRate2" aria-labelledby="introIntRate2" size={15} />
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td><label htmlFor="introMonths1"><strong>Term of introductory rate (# months - optional):</strong></label></td>
                                                            <td><input type="TEXT" name="introMonths1" id="introMonths1" aria-labelledby="introMonths1" size={15} /></td>
                                                            <td>
                                                                <label htmlFor="introMonths2" className="hide-label"><strong>Term of introductory rate (# months - optional): &nbsp;</strong></label>
                                                                <input type="TEXT" name="introMonths2" id="introMonths2" aria-labelledby="introMonths2" size={15} />
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td><label htmlFor="regIntRate1"><strong>Enter the regular annual interest rate:</strong></label></td>
                                                            <td><input type="TEXT" name="regIntRate1" id="regIntRate1" aria-labelledby="regIntRate1" size={15} /></td>
                                                            <td>
                                                                <label htmlFor="regIntRate2" className="hide-label"><strong>Enter the regular annual interest rate: &nbsp;</strong></label>
                                                                <input type="TEXT" name="regIntRate2" id="regIntRate2" aria-labelledby="regIntRate2" size={15} />
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td><label htmlFor="compound1"><strong>Enter the compounding period:</strong></label></td>
                                                            <td><select name="compound1" id="compound1" aria-labelledby="compound1" size={1} style={{ width: "8em", height:"2em" }}>
                                                                <option value="0">Monthly</option>
                                                                <option value="1">Daily</option>
                                                            </select>
                                                            </td>
                                                            <td>
                                                                <label htmlFor="compound2" className="hide-label"><strong>Enter the compounding period: &nbsp;</strong></label>
                                                                <select
                                                                    name="compound2"
                                                                    id="compound2"
                                                                    aria-labelledby="compound2"
                                                                    size={1}
                                                                    style={{ width: "8em", height: "2em" }}
                                                                >
                                                                    <option value="0">Monthly &nbsp;</option>
                                                                    <option value="1">Daily</option>
                                                                </select>
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td colSpan={2}></td>
                                                            <td style={{ float: "right" }}>
                                                                <input
                                                                    className="debt_textbtn"
                                                                    type="submit"
                                                                    value="Compute"
                                                                    style={{ width: "70px" }}
                                                                    onClick={(e) => window.computeForm(e)}
                                                                />
                                                                &nbsp;
                                                                <input className="debt_textbtn" type="reset" value="Reset" style={{ width: "60px" }} />
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td className="calc_divider"><label htmlFor="nPer1"><strong>Number of payments till paid off:</strong></label></td>
                                                            <td className="calc_divider"><input type="TEXT" name="nPer1" id="nPer1" aria-labelledby="nPer1" /></td>
                                                            <td className="calc_divider">
                                                                <label htmlFor="nPer2" className="hide-label"><strong>Number of payments till paid off: &nbsp;</strong></label>
                                                                <input type="TEXT" name="nPer2" id="nPer2" aria-labelledby="nPer2" />
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td><label htmlFor="totalCosts1"><strong>Total costs:</strong></label></td>
                                                            <td><input type="TEXT" name="totalCosts1" id="totalCosts1" aria-labelledby="totalCosts1" size={15} /></td>
                                                            <td>
                                                                <label htmlFor="totalCosts2" className="hide-label"><strong>Total costs: &nbsp;</strong></label>
                                                                <input type="TEXT" name="totalCosts2" id="totalCosts2" aria-labelledby="totalCosts2" size={15} />
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Helmet>
                <script src="js/CreditCardComparisonCalculator.js" type="text/javascript" />
            </Helmet>
        </React.Fragment>
    );
};
export default CreditCardComparisonCalculator;
